import { LunaCodeNode } from "../../common/models/rules.model";
import { AsyncExpressionCompiler, ExpressionCompiler, FunctionDefinition } from "../../common/services/expression.compiler";

import StaticPromise from "../../common/models/static-promise";
import { ValidationErrors, AbstractControl } from "@angular/forms";
import { Rule, ValidationRule, Question } from "../../common/models/flow.dto";

import { Injectable, Injector } from "@angular/core";

import { GetDomain, ArrayContains, ArrayMap, ArrayOf, ArrayReduce, Coalesce, DateAdd, DateDiff, DatePart, FormatDate, Filter, Now, ParseFloat, RegexMatch, StringConcat, StringLen, StringLower, StringReplace, StringSubstring, StringTrimLeft, StringTrim, StringUpper, Today, GetProperty, BitwiseAND, BitwiseOR, BitwiseXOR} from "../../common/services/functions";

import { RuleEvaluationFn } from "../../flow/models/flow.model";

import { DomainDefinitionService } from "../../builder/services/domain-definition.service";

@Injectable()
export class FunctionsFactory{
    constructor(public injector:Injector){

    }
    public getFunctions(): FunctionDefinition[] { 
        const result = [
            new ArrayContains(),
            new ArrayMap(),
            new ArrayOf(),
            new ArrayReduce(),
            new DateAdd(),
            new DateDiff(),
            new DatePart(),
            new FormatDate(),
            new Filter(),
            new Now(),
            new ParseFloat(),
            new RegexMatch(),
            new StringConcat(),
            new StringLen(),
            new StringLower(),
            new StringSubstring(),
            new StringTrimLeft(),
            new StringTrim(),
            new StringUpper(),
            new Today(),
            new GetProperty(),  
            new BitwiseAND(),
            new BitwiseOR(),
            new BitwiseXOR(),
            new Coalesce(),
            new StringReplace()
        ];
        if(this.injector){
            result.push(new GetDomain(this.injector.get(DomainDefinitionService)));
        }
        return result;


    }
}


@Injectable()

export class RuleCompiler {


    _asyncCompiler: AsyncExpressionCompiler;
    _syncCompiler: ExpressionCompiler;

// make sure that wherever the provider for this is registerd imports domain definition service
    constructor(functionsFactory:FunctionsFactory){
        let functions = functionsFactory.getFunctions();
        this._asyncCompiler = new AsyncExpressionCompiler(functions);
        this._syncCompiler = new ExpressionCompiler(functions);
    }
    public compileSync<T>(root: LunaCodeNode): (flowContext: any) => T {
        let compiled = this._syncCompiler.compile(root);
        let result = (flowContext: any) => compiled({
            context: flowContext,
            args: new Map<string, any>()
        }) as T;
        return result;
    }

    public compileAsync<T>(root: LunaCodeNode): (flowContext: any) => Promise<T> {
        let compiled = this._asyncCompiler.compile(root);
        return (flowContext: any) => compiled({
            context: flowContext,
            args: new Map<string, any>()
        }) as Promise<T>;
    }

    public buildConstantAsyncRuleResult<T>(value: T): () => Promise<T> {
        return () => new Promise<T>((resolve, reject) => resolve(value));
    }

    public buildConstantSyncRuleResult<T>(value: T): () => T {
        return () => value;
    }

    compileBooleanRule(rule: Rule, context: any): () => boolean {
        if (rule) {
            let capture = this.compileSync<boolean>(rule.parseTree);
            return () => capture(context);
        } else {
            return () => true;
        }
    }

    compileAsyncRule<T>(rule: Rule, context: any, defaultValue: T): () => Promise<T> {
        if (rule) {
            let capture = this.compileAsync<T>(rule.parseTree);
            return () => capture(context);
        } else {
            return this.buildConstantAsyncRuleResult<T>(defaultValue);
        }
    }

    compileSyncRule<T>(rule: Rule, context: any, defaultValue: T): () => T {
        if (rule) {
            let capture = this.compileSync<T>(rule.parseTree);
            return () => capture(context);
        } else {
            return this.buildConstantSyncRuleResult<T>(defaultValue);
        }
    }

    compileSyncRuleFunc<T>(rule: Rule, defaultValue: T): (ctx:any) => T {
        if (rule) {
            let capture = this.compileSync<T>(rule.parseTree);

            return capture;
        } else {
            return () => defaultValue;
        }
    }

    compileValidationRules(validationRules: ValidationRule[], context: any): any {
        if (validationRules) {
            const promiseFactories = validationRules.map(r => this.compileAsync<boolean>(r.parseTree));

            let validator = (ctrl: AbstractControl) => {
                let promises = promiseFactories.map(f => f(context));
                return Promise.all(promises)
                    .then(valids => {
                        let result = null;
                        for (let i = 0; i < valids.length; i++) {
                            if (!valids[i]) {
                                result = result || {};

                                result[validationRules[i].key] = validationRules[i].message;
                            }
                        }

                        ctrl.setErrors(result);
                    });
            };
            return {
                pageValidationMessages: validationRules.map(r => r.message),
                pageValidator: validator
            };
        } else {
            //this branch seems to result in validation that is always invalid
            return {
                pageValidationMessages: [],
                pageValidator: (c: AbstractControl) => StaticPromise.of<ValidationErrors>({})
            };
        }
    }

    compileFlowValidationRules(question: Question, context: any): any {
            const validation = question.validationRules;
            const enabled = (question.enabledRule)
                ? this.compileAsync<boolean>(question.enabledRule.parseTree)
                : this.buildConstantAsyncRuleResult<boolean>(true);
            const visible = (question.visibleRule)
                ? this.compileAsync<boolean>(question.visibleRule.parseTree)
                : this.buildConstantAsyncRuleResult<boolean>(true);
        const validations = validation.map(r => this.compileAsync<boolean>(r.parseTree));
            const validator = (ctrl: AbstractControl) => {
                const promises = [];
                promises.push(visible(context));
                promises.push(enabled(context));
                const validationPromises = validations.map(v => v(context));
                validationPromises.forEach(v => promises.push(v));
                return Promise.all(promises)
                    .then(valids => {        
                            //valids[0] should be the VISIBLE result; if not visible, ignore everything else
                            //valids[1] should be the ENABLED result; if visible but not enabled, ignore everything else
                            if (!valids[0] || (valids[0] && !valids[1])) {
                                ctrl.setErrors(null);
                            } else {
                                let result = null;
                                for (let v = 2; v < valids.length; v++) {
                                    if (!valids[v]) {
                                        result = result || {};
                                        result[validation[v - 2].key] = validation[v - 2].message;
                                    }
                                }
                                ctrl.setErrors(result);
                            }
                    });
            };
            return {
                flowValidationMessages: validation.map(r => r.message),
                flowValidator: validator
            };
    }

    compilePagePartiallyRequiredRule(questions: Question[], context: any): RuleEvaluationFn<boolean> {
        if (questions) {
			const rules = questions.map(q => {
				const enabled = (q.enabledRule)
					? this.compileBooleanRule(q.enabledRule, context)
					: (c: any) => { return true; };
				const visible = (q.visibleRule)
					? this.compileBooleanRule(q.visibleRule, context)
					: (c: any) => { return true; };
				const validation: ((c: any) => boolean)[] = (q.validationRules)
					? q.validationRules.map(r => this.compileBooleanRule(r, context))
					: [
						(c: any) => { return true; }
					];
				return (c: any): boolean => {
					if (visible(c) && enabled(c)) {
						const results = validation.map(v => v(c));
						return results.find(r => !r) === undefined;
					} else {
						return true;
					}
				}
			});
            return (): boolean => {
                const results = rules.map(r => r(context));
                return results.find(r => !r) === undefined;
            }
        } else {
            return (): boolean => { return true };
        }
    }
}